import classNames from "classnames";
import React from "react";
import { ChannelHeader } from "components/ChannelHeader/ChannelHeader";
import { MessageBubble } from "components/MessageBubble/MessageBubble";
import { MessageInput } from "components/MessageInput/MessageInput";
import { SMALL } from "constants/chatBubbleWidths";
import {
  ACTIVITY_TAB,
  CHAT_TAB,
  TAB_PARAM,
  ChatTabType,
} from "constants/chatTab";
import dayjs from "dayjs";
import isToday from "dayjs/plugin/isToday";
import isYesterday from "dayjs/plugin/isYesterday";
import localizedFormat from "dayjs/plugin/localizedFormat";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useDropzone } from "react-dropzone";
import { useDispatch } from "react-redux";
import uploadCloud from "static/images/uploadCloud.svg";
import { TaskChannel, useGetTaskFromChannelIdQuery } from "store/apis/task";
import { setDropedFile, setUserLastMessageId } from "store/slices/chat";
import {
  Channel as StreamChannel,
  getGroupStyles,
  MessageInput as StreamInput,
  MessageList,
  useChatContext,
  Window,
  VirtualizedMessageList,
  TypingIndicatorProps,
  useChannelStateContext,
} from "stream-chat-react";
import { JumpToMessage } from "./JumpToMessage";
import { JumpToFirstUnreadMessage } from "./JumpToFirstUnreadMessage";
import { useGetUnresolvedTicketMessageIdsQuery } from "store/apis/chat";
import { useQuery } from "hooks/useQuery";
import { useSelector } from "react-redux";
import { RootState } from "store/store";
import { setScrollToMessageId } from "store/slices/messageToScrollTo";
import { setShowOnlyAdminMessage } from "store/slices/chatSearch";
import {
  AI_CHANNEL,
  ANNOUNCEMENTS_CHANNEL,
  KB_SEARCH_CHANNEL,
} from "constants/chatType";
import DeleteMessageModal from "components/MessageBubble/DeleteMessage";
import { ArrayElement } from "types/utils/ArrayElement";
import { useDeviceInfo } from "hooks/useDeviceInfo";
import { FIREFOX } from "constants/deviceInfo";
import { NoChannelSelected } from "components/TPPayments/Illustration/NoChannelSelected";
import { DocuSignModal } from "components/RequestSign/DocuSignModal";
import { ChatTicketModal } from "components/MessageBubble/ChatTicketModal";
import { ClarifyModal } from "components/OpenItem/ClarifyModal";
import { AddOpenItem } from "components/OpenItem/AddOpenItem";
import { DateSeparator } from "./DateSeparator";
import { TypingIndicator } from "./TypingIndicator";
import { SystemMessage } from "./SystemMessage";
import { EmptyStateIndicator } from "./EmptyStateIndicator";
import { MentionTriggerProvider } from "components/MentionSuggestion/MentionTriggerProvider";
import { AuditlogList } from "components/AuditlogList/AuditlogList";
import { useCurrentChatGroupContext } from "hooks/useCurrentChatGroupContext";
import { CurrentChatGroupContextProvider } from "contexts/CurrentChatGroupContext";
import { useGetGroupDataQuery } from "store/apis/group";
import { EmailSuppressionBanner } from "components/EmailSuppressionBanner/EmailSuppressionBanner";

dayjs.extend(isYesterday);
dayjs.extend(isToday);
dayjs.extend(localizedFormat);

type CustomChannelProps = {
  channelId?: string;
  Header?: React.ComponentType<{
    isPrivateChannel: boolean;
    setPrivateChannel: any;
    showOnlyAdminMessage?: boolean;
    setShowOnlyAdminMessage: (val: boolean) => void;
    activeTab?: ChatTabType;
    setActiveTab?: (tab: ChatTabType) => void;
  }>;
  messageMaxWidth?: string;
  isTicket?: boolean;
  children?: React.ReactNode;
  messageId?: string;
  disableAutoFocus?: boolean;
  messageSource?: string;
};

export const getPrivateOrPublicChannelForCurrentChannel = (
  task?: ArrayElement<TaskChannel["tasks"]>,
  companyGroup?: TaskChannel["company_group"],
  currentChannel?: string,
  isPrivate?: boolean
) => {
  const returnPublic = !isPrivate;
  const returnPrivate = isPrivate;

  const isTaskChannel =
    task?.channel.channel_url === currentChannel ||
    task?.private_channel.channel_url === currentChannel;

  const isBookkeepingChannel = currentChannel
    ? Object.values(companyGroup?.books_channels || {})
        .map((c) => c.channel_url)
        .includes(currentChannel)
    : false;

  const isAnnouncementChannel =
    companyGroup?.announcements_channel.channel_url === currentChannel;
  const isMailRoomChannel =
    companyGroup?.mailbox_channel.channel_url === currentChannel;

  const isGroupLevelChannel =
    isBookkeepingChannel ||
    isAnnouncementChannel ||
    isMailRoomChannel ||
    !isTaskChannel;

  const isCurrentChannelPrivate =
    companyGroup?.private_channel.channel_url === currentChannel;

  const privateChannel = isTaskChannel
    ? task?.private_channel.channel_url
    : companyGroup?.private_channel.channel_url;

  let publicChannel = isGroupLevelChannel
    ? currentChannel
    : task?.channel.channel_url;

  if (returnPrivate && privateChannel) {
    return privateChannel;
  }

  if (returnPublic && isCurrentChannelPrivate) {
    return companyGroup?.secondary_channel.channel_url;
  }

  if (returnPublic && isGroupLevelChannel) {
    return currentChannel;
  }

  if (returnPublic && publicChannel) {
    return publicChannel;
  }

  return currentChannel;
};

const FilteredMessageList = ({
  showOnlyAdminMessage,
  formatDate,
}: {
  showOnlyAdminMessage: boolean;
  formatDate: (date: Date) => string;
}) => {
  const { messages, channel } = useChannelStateContext();

  const customerMemberIds = Object.entries(channel?.state?.members || {})
    .filter(([, value]) => !Boolean(value?.user?.service_team))
    .map(([, value]) => value?.user?.id);

  const filteredMessages = useMemo(() => {
    return messages?.filter((msg) => {
      if (!msg.user) return true;

      if (showOnlyAdminMessage) {
        return !customerMemberIds.includes(msg.user.id);
      }

      return customerMemberIds.includes(msg.user.id);
    });
  }, [messages, showOnlyAdminMessage]);

  return (
    <MessageList
      messages={filteredMessages}
      formatDate={formatDate}
      // They only expect a certain type but we need to pass our class name also
      // @ts-ignore
      groupStyles={(...args: any) =>
        // @ts-ignore
        `${getGroupStyles(...args)}  t-group/${getGroupStyles(...args)}`
      }
    />
  );
};

const SyncChannelGroupContext = ({ groupId }: { groupId: string }) => {
  const { dispatch: groupDispatch } = useCurrentChatGroupContext();
  const { data: channelGroupData } = useGetGroupDataQuery({ groupId });

  useEffect(() => {
    if (channelGroupData) {
      groupDispatch({ type: "SET_GROUP", payload: channelGroupData });
    }
  }, [channelGroupData]);

  return null;
};

const ChannelInner = ({
  channelId: propsChannelId,
  Header = ChannelHeader,
  isTicket,
  messageMaxWidth = SMALL,
  children,
  messageId,
  disableAutoFocus,
  messageSource,
}: CustomChannelProps) => {
  const dispatch = useDispatch();
  const { client, channel: currentChannel } = useChatContext();
  const history = useHistory();
  const { search } = useLocation();
  const [isPrivate, setIsPrivate] = useState(false);
  const tabFromUrl = new URLSearchParams(search).get(
    TAB_PARAM
  ) as ChatTabType | null;
  const [activeTab, setActiveTab] = useState<ChatTabType>(
    tabFromUrl === CHAT_TAB || tabFromUrl === ACTIVITY_TAB
      ? tabFromUrl
      : CHAT_TAB
  );

  useEffect(() => {
    const tab = new URLSearchParams(search).get(TAB_PARAM);
    if (tab === CHAT_TAB || tab === ACTIVITY_TAB) {
      setActiveTab(tab);
    }
  }, [search]);

  const handleSetActiveTab = (tab: ChatTabType) => {
    setActiveTab(tab);
    const params = new URLSearchParams(search);
    params.set(TAB_PARAM, tab);
    history.replace({ search: params.toString() });
  };
  const [channelToShow, setChannelToShow] = useState<string>();
  const { browser } = useDeviceInfo();
  const { isAdmin, isCustomer } = useRoleBasedView();
  const [isLoading, setIsLoading] = useState(true);
  const { showAIChannels, showOnlyAdminMessage } = useSelector(
    (state: RootState) => state.chatSearch
  );

  const handleSetShowOnlyAdminMessage = (value: boolean) => {
    dispatch(setShowOnlyAdminMessage(value));
  };

  const MessageInputComponent = useMemo(
    () => (props: any) =>
      (
        <MessageInput
          {...props}
          disableAutoFocus={disableAutoFocus}
          messageSource={messageSource}
        />
      ),
    [disableAutoFocus, messageSource]
  );

  const userLastMessageId = (() => {
    const messages = currentChannel?.state.messages || [];
    const userMessages = messages.filter(
      (msg: any) => msg.user && msg.user.id === client.userID
    );
    return userMessages[userMessages.length - 1]?.id || null;
  })();

  useEffect(() => {
    dispatch(setUserLastMessageId(userLastMessageId));
  }, [userLastMessageId]);

  const messageToScrollTo =
    useSelector((state: RootState) => state.messageToScrollTo.messageId) ||
    messageId;

  const { data: channelTask } = useGetTaskFromChannelIdQuery(
    {
      channelId: (propsChannelId || currentChannel?.id)!,
    },
    {
      skip: !(propsChannelId || currentChannel?.id),
    }
  );

  const { tasks, company_group: companyGroup } = channelTask || {};
  const task = tasks?.[0];

  const formatDate = (date: Date) => {
    const formattedDate = dayjs(date);

    if (formattedDate.isToday()) {
      return `Today at ${formattedDate.format("LT")}`;
    }

    if (dayjs(date).isYesterday()) {
      return `Yesterday at ${formattedDate.format("LT")}`;
    }

    return formattedDate.format("MMM DD, YYYY");
  };

  const onDrop = async (files: File[]) => {
    dispatch(setDropedFile(files));
  };

  const { getRootProps, isDragAccept } = useDropzone({
    noClick: true,
    noKeyboard: true,
    onDrop,
  });

  const { data: unresolvedTickets } = useGetUnresolvedTicketMessageIdsQuery(
    {
      channelUrl: currentChannel?.id!,
    },
    {
      skip: (!isAdmin && !isCustomer) || !currentChannel?.id,
    }
  );
  const query = useQuery();
  const currentTicket = query.get("ticketUuid");

  const ticketMessageId = useMemo(() => {
    return (
      unresolvedTickets?.ticket_messages.find(
        (e) => e.ticket_uuid === currentTicket
      )?.message_id ?? null
    );
  }, [currentTicket, unresolvedTickets]);

  let channelId = propsChannelId || currentChannel?.id;

  const setPrivateChannel = () => {
    setIsPrivate((v) => {
      setIsLoading(true);
      const newChannelId = getPrivateOrPublicChannelForCurrentChannel(
        task,
        companyGroup,
        channelId,
        !v
      );

      setChannelToShow(newChannelId);
      setIsLoading(false);

      return !v;
    });
  };

  useEffect(() => {
    const isCurrentPrivateChannel =
      task?.private_channel.channel_url === channelId ||
      companyGroup?.private_channel.channel_url === channelId;

    setIsPrivate(isCurrentPrivateChannel);
  }, [
    channelId,
    companyGroup?.private_channel.channel_url,
    task?.private_channel.channel_url,
  ]);

  useEffect(() => {
    setChannelToShow(channelId);
    setIsLoading(false);
  }, [channelId]);

  const channel = useMemo(() => {
    if (channelToShow) {
      return client?.channel("messaging", channelToShow);
    }
  }, [channelToShow, client]);

  const isKBChannel = channel?.data?.custom_type === KB_SEARCH_CHANNEL;

  // Track previous showAIChannels to detect changes
  const prevShowAIChannelsRef = useRef(showAIChannels);

  // Clear channel when AI filter is toggled and current channel doesn't match
  useEffect(() => {
    const prevShowAIChannels = prevShowAIChannelsRef.current;
    prevShowAIChannelsRef.current = showAIChannels;

    // Only act when showAIChannels actually changed
    if (prevShowAIChannels === showAIChannels) return;
    if (!channelToShow || !channel) return;

    const aiFilterJustEnabled = !prevShowAIChannels && showAIChannels;
    if (aiFilterJustEnabled && propsChannelId) return;

    const isCurrentChannelAI = channel?.data?.custom_type === AI_CHANNEL;

    // When AI filter is ON but current channel is not AI, clear it
    // When AI filter is OFF but current channel is AI, clear it
    const shouldClearChannel =
      (showAIChannels && !isCurrentChannelAI) ||
      (!showAIChannels && isCurrentChannelAI);

    if (shouldClearChannel) {
      setChannelToShow(undefined);
    }
  }, [showAIChannels, channelToShow, channel]);

  useEffect(() => {
    if (ticketMessageId) {
      dispatch(setScrollToMessageId(ticketMessageId));
    }
  }, [dispatch, ticketMessageId]);

  useEffect(() => {
    let timer: NodeJS.Timeout;

    timer = setTimeout(() => {
      dispatch(setScrollToMessageId());
    }, 2000);

    return () => clearTimeout(timer);
  }, [messageToScrollTo]);

  if (!client || isLoading) {
    return (
      <div className="t-flex t-items-center t-justify-center t-h-full t-w-full t-bg-surface-grey t-gap-5 t-flex-col">
        <div className="t-flex t-flex-col t-gap-1 t-text-center">
          <p className="t-m-0 t-text-subtext-sm t-animate-pulse-fast">
            Loading...
          </p>
        </div>
      </div>
    );
  }

  if (!channelToShow) {
    return (
      <div className="t-flex t-items-center t-justify-center t-h-full t-w-full t-bg-surface-grey t-gap-5 t-flex-col">
        <NoChannelSelected />
        <div className="t-flex t-flex-col t-gap-1 t-text-center">
          <p className="t-m-0 t-text-subtext-sm">No channel selected yet</p>
          <p className="t-m-0 t-text-body-sm">
            Select a channel to get started
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      // @ts-ignore
      style={{ "--message-max-width": messageMaxWidth }}
      {...getRootProps()}
      className={classNames("[&_[class*=list-notifications]:empty]:t-hidden", {
        "t-h-full": !isTicket,
        "t-h-[calc(100%-98px)]": isTicket,
      })}
    >
      {isDragAccept && (
        <div
          className={classNames(
            "t-absolute t-z-10 t-flex  t-flex-col t-items-center t-justify-center t-gap-3 t-border t-border-dashed t-border-neutral-70 t-bg-[#f3f3f580]",
            {
              "t-h-[-webkit-fill-available] t-w-[-webkit-fill-available]":
                browser !== FIREFOX,
              "t-h-full t-w-[-moz-available]": browser === FIREFOX,
            }
          )}
        >
          <img
            src={uploadCloud}
            alt="upload"
            className="t-animate-drag-and-drop"
          />
          <div className="custom-chat-upload-drop-card">
            Drop files to upload them
          </div>
        </div>
      )}
      <StreamChannel
        Message={(props) => <MessageBubble />}
        Input={MessageInputComponent}
        channel={channel}
        // @ts-ignore
        channelQueryOptions={{ member_limit: 100 }}
        DateSeparator={DateSeparator}
        TypingIndicator={TypingIndicator}
        MessageSystem={SystemMessage}
        EmptyStateIndicator={EmptyStateIndicator}
        ThreadHeader={() => null}
        ThreadStart={() => null}
        TriggerProvider={MentionTriggerProvider}
      >
        {!isCustomer && (channel?.data?.group_uuid || companyGroup?.uuid) && (
          <SyncChannelGroupContext
            groupId={
              (channel?.data?.group_uuid as string) || companyGroup!.uuid
            }
          />
        )}
        <DocuSignModal />
        <ClarifyModal />
        <AddOpenItem />
        {messageToScrollTo && !isKBChannel && (
          <JumpToMessage messageId={messageToScrollTo} />
        )}
        {!messageToScrollTo && !isKBChannel && <JumpToFirstUnreadMessage />}
        <Window hideOnThread>
          <EmailSuppressionBanner channelId={channelId} isAdmin={isAdmin} />
          {!isTicket && (
            <Header
              isPrivateChannel={isPrivate}
              setPrivateChannel={setPrivateChannel}
              showOnlyAdminMessage={showOnlyAdminMessage}
              setShowOnlyAdminMessage={handleSetShowOnlyAdminMessage}
              activeTab={activeTab}
              setActiveTab={handleSetActiveTab}
            />
          )}

          {activeTab === CHAT_TAB ? (
            <>
              {showAIChannels ? (
                <FilteredMessageList
                  showOnlyAdminMessage={showOnlyAdminMessage}
                  formatDate={formatDate}
                />
              ) : (
                <MessageList
                  formatDate={formatDate}
                  // @ts-ignore // They only expect a certain type but we need to pass our class name also
                  groupStyles={(...args) =>
                    `${getGroupStyles(...args)}  t-group/${getGroupStyles(
                      ...args
                    )}`
                  }
                />
              )}
              {((currentChannel?.data?.custom_type !== ANNOUNCEMENTS_CHANNEL &&
                currentChannel?.data?.custom_type !== AI_CHANNEL) ||
                (showAIChannels && showOnlyAdminMessage)) && <StreamInput />}
            </>
          ) : (
            <div className="t-h-full t-overflow-y-auto t-bg-white t-pt-5 t-border-0 t-border-t-0.5 t-border-solid t-border-neutral-10">
              {channelTask?.entity?.uuid && task?.uuid ? (
                <AuditlogList
                  entityId={channelTask.entity.uuid}
                  objectId={task.uuid}
                />
              ) : (
                <div className="t-flex t-h-full t-items-center t-justify-center t-text-text-30">
                  No activity to show
                </div>
              )}
            </div>
          )}
        </Window>

        {children}
        <ChatTicketModal />
      </StreamChannel>
      {isAdmin && <DeleteMessageModal />}
    </div>
  );
};

export const Channel = (props: CustomChannelProps) => (
  <CurrentChatGroupContextProvider>
    <ChannelInner {...props} />
  </CurrentChatGroupContextProvider>
);
